import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SYNC_INSURANCE } from '@constants/index'
import { AppErrorType } from '@typesApp/AppError'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { InsuranceStatusErrorCode, InsuranceUsage } from '@typesApp/ClaimData'
import { useClaimRecord } from '@hooks/useClaimRecord'
import { useEnvironment } from '@hooks/useEnvironment'
import { useValidateSession } from '@hooks/useValidateSession'
import { usePollClaimRecordV2 } from '@hooks/usePollClaimRecord'
import { setAppError } from '@redux/slices/workflowSlice'
import { connectorActionModuleSelector } from '@redux/slices/connectorSlice'
import { useChangeInsuranceStatusMutation } from '@redux/queries/claimRecords'
import { appEventEmitter } from '@utils/appEventEmitter'
import { resetClaimRecordState } from '@utils/resetClaimRecordState'
import {
  isConflictError,
  isErrorWithClaimRecord,
  isGatewayTimeout,
  isResourceNotFoundError,
  isSessionExpiredError,
  isSessionTransitionedError,
} from '@utils/errorUtil'
import { useInteractionContext } from '@components/interactionContext/useInteractionContext'
import { useInvokeSyncInsuranceListeners } from './useInvokeSyncInsuranceListener'

const STOP_ERROR_CODES: InsuranceStatusErrorCode[] = [
  InsuranceStatusErrorCode.CART_QUOTE_IN_PROGRESS,
  InsuranceStatusErrorCode.CLAIM_NULLIFY_IN_PROGRESS,
  InsuranceStatusErrorCode.CLAIM_SUBMIT_IN_PROGRESS,
  InsuranceStatusErrorCode.CART_QUOTE_FAILED,
]

type UseInsuranceUsageReturn = {
  isLoading: boolean
  enableInsuranceUsage: () => Promise<void>
  disableInsuranceUsage: () => Promise<void>
}

export const useInsuranceUsage = (): UseInsuranceUsageReturn => {
  const dispatch = useDispatch()
  const { cartPath } = useEnvironment()
  const [isLoading, setLoading] = useState(false)
  const { claimRecord } = useClaimRecord()
  const { checkSessionValid } = useValidateSession()
  const { pushFallbackInteraction } = useInteractionContext()
  const { pollClaimRecord } = usePollClaimRecordV2(SYNC_INSURANCE.MAX_WAIT_TIME_TOGGLE_MS)
  const invokeListeners = useInvokeSyncInsuranceListeners()
  const actionModule = useSelector(connectorActionModuleSelector)
  const [triggerChangeStatus] = useChangeInsuranceStatusMutation()

  const enableInsuranceUsage = (): Promise<void> => changeInsuranceUsage(InsuranceUsage.INSURANCE_ENABLED)

  const disableInsuranceUsage = (): Promise<void> => changeInsuranceUsage(InsuranceUsage.INSURANCE_DISABLED)

  const changeInsuranceUsage = async (insuranceUsage: InsuranceUsage): Promise<void> => {
    if (!claimRecord) return

    const isResultReady = (claimRecord: ClaimRecord): boolean => {
      return (
        claimRecord?.claimData?.useInsuranceStatus === insuranceUsage ||
        STOP_ERROR_CODES.includes(claimRecord?.claimData?.insuranceStatusErrorCode ?? InsuranceStatusErrorCode.NONE)
      )
    }

    const handleResult = (claimRecord: ClaimRecord, isTimeExceeded?: boolean) => {
      if (isTimeExceeded) {
        handleError()
        return
      }

      setLoading(false)

      // do not show error page if claim application still in progress
      if (
        STOP_ERROR_CODES.includes(claimRecord?.claimData?.insuranceStatusErrorCode ?? InsuranceStatusErrorCode.NONE)
      ) {
        return
      }

      const isSuccess = claimRecord?.claimData?.useInsuranceStatus === insuranceUsage
      if (isSuccess) {
        triggerCommerceAction()
      }
    }

    const handleError = async (error?: unknown) => {
      setLoading(false)

      // On session expired let toggle revert to initial state.
      // For all errors redirect to error page

      if (isSessionExpiredError(error) || isSessionTransitionedError(error)) {
        triggerCommerceAction()
      } else if (isResourceNotFoundError(error)) {
        resetClaimRecordState(dispatch)

        const isValid = await checkSessionValid()
        !isValid && appEventEmitter.sessionExpired()

        triggerCommerceAction()
      } else {
        const isEnable = insuranceUsage === InsuranceUsage.INSURANCE_ENABLED
        pushFallbackInteraction('fallback', {
          reason: isEnable ? 'enable-insurance-error' : 'disable-insurance-error',
          error,
        })
        dispatch(setAppError(AppErrorType.GENERIC_ERROR))
      }
    }

    const triggerCommerceAction = () => {
      if (insuranceUsage === InsuranceUsage.INSURANCE_ENABLED || insuranceUsage === InsuranceUsage.INSURANCE_DISABLED) {
        const action = insuranceUsage === InsuranceUsage.INSURANCE_ENABLED ? 'sync' : 'remove'
        invokeListeners(action)
      }

      // eslint-disable-next-line no-restricted-globals
      const isCart = window.location.pathname.includes(cartPath)
      if (isCart) {
        actionModule?.refreshCart()
      }
    }

    const pollClaimRecordForResult = () => {
      pollClaimRecord(isResultReady, handleResult, handleError)
    }

    const doUpdateWithPolling = async () => {
      try {
        const result = await triggerChangeStatus({
          claimRecordId: claimRecord.id,
          useInsuranceStatus: insuranceUsage,
        }).unwrap()

        if (isResultReady(result)) {
          handleResult(result)
        } else {
          pollClaimRecordForResult()
        }
      } catch (error) {
        handleSubmitError(error)
      }
    }

    const handleSubmitError = (error?: unknown) => {
      // b/e will return 409 if status cannot be changed due
      // to cart quote or claim submission in progress
      if (isGatewayTimeout(error) || isConflictError(error)) {
        pollClaimRecordForResult()
      } else {
        if (isErrorWithClaimRecord(error) && error?.data?.claimRecord) {
          handleResult(error.data.claimRecord)
        } else {
          handleError(error)
        }
      }
    }

    // update insurance usage and poll as required
    setLoading(true)
    doUpdateWithPolling()
  }

  return {
    isLoading,
    enableInsuranceUsage,
    disableInsuranceUsage,
  }
}
